import React from 'react';
import { Space, Tooltip, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { usePharmacyNoteConfigs } from './usePharmacyNoteConfigs';
import { ConfigModal } from './ConfigModal';
import {
    PageWrapper,
    PageHeader,
    PageTitle,
    PageSubtitle,
    InfoBanner,
    BannerCode,
    StyledTable,
    ConfigLabelText,
    TagList,
    TemplateCode,
    MaxCharsText,
    AddButton,
    EditButton,
    DeleteButton,
    IdTag,
    FieldLabel,
    PopconfirmSubtitle,
} from './styles';

const DEFAULT_MAX_CHARACTERS = 210;

const PharmacyNoteConfigs = () => {
    const {
        configs,
        loading,
        modalVisible,
        editingConfig,
        openCreate,
        openEdit,
        handleDelete,
        handleModalSuccess,
        closeModal,
    } = usePharmacyNoteConfigs();

    const columns = [
        {
            title: <FieldLabel>Config Label</FieldLabel>,
            dataIndex: 'pharmacyName',
            key: 'pharmacyName',
            sorter: (a, b) => a.pharmacyName.localeCompare(b.pharmacyName),
            render: (name) => <ConfigLabelText>{name}</ConfigLabelText>,
        },
        {
            title: <FieldLabel>Pharmacy IDs</FieldLabel>,
            dataIndex: 'pharmacyIds',
            key: 'pharmacyIds',
            render: (ids) => (
                <TagList>
                    {ids?.map((id) => <IdTag key={id}>{id}</IdTag>)}
                </TagList>
            ),
        },
        {
            title: <FieldLabel>Note Template</FieldLabel>,
            dataIndex: 'noteTemplate',
            key: 'noteTemplate',
            render: (template) => <TemplateCode>{template}</TemplateCode>,
        },
        {
            title: <FieldLabel>Max Chars</FieldLabel>,
            dataIndex: 'maxCharacters',
            key: 'maxCharacters',
            width: 100,
            align: 'center',
            render: (val) => <MaxCharsText>{val}</MaxCharsText>,
        },
        {
            title: <FieldLabel>Actions</FieldLabel>,
            key: 'actions',
            width: 90,
            align: 'center',
            render: (_, record) => (
                <Space size={8}>
                    <Tooltip title="Edit">
                        <EditButton
                            shape="circle"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={
                            <>
                                <strong>Delete this config?</strong>
                                <PopconfirmSubtitle>
                                    Prescriptions for these pharmacies will fall back to the default note format.
                                </PopconfirmSubtitle>
                            </>
                        }
                        onConfirm={() => handleDelete(record._id)}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Delete">
                            <DeleteButton shape="circle" size="small" icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <PageWrapper>
            <PageHeader>
                <div>
                    <PageTitle>Pharmacy Note Configurations</PageTitle>
                    <PageSubtitle>
                        Define how pharmacy notes are composed for each pharmacy at time of prescribing.
                        Pharmacies not listed here fall back to the default format.
                    </PageSubtitle>
                </div>
                <AddButton icon={<PlusOutlined />} onClick={openCreate}>
                    Add Config
                </AddButton>
            </PageHeader>

            <InfoBanner>
                <strong>Default fallback template:</strong>{' '}
                <BannerCode>{'{{pharmacyNotes}} - Brand: {{clientName}}, LTC {{date}}'}</BannerCode>
                {' '}— applied to any pharmacy without a config, with a {DEFAULT_MAX_CHARACTERS}-character limit.
            </InfoBanner>

            <StyledTable
                rowKey="_id"
                columns={columns}
                dataSource={configs}
                loading={loading}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                size="middle"
            />

            <ConfigModal
                open={modalVisible}
                onCancel={closeModal}
                onSuccess={handleModalSuccess}
                editingConfig={editingConfig}
            />
        </PageWrapper>
    );
};

export default PharmacyNoteConfigs;
